import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { TFunction } from "i18next";
import { TASK_TRIGGER_PERIODICALLY } from "shared/api";

// dayjs plugins are global once registered; extending here means any module that
// imports a helper from this file gets UTC/timezone support without repeating
// the boilerplate.
dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_THEME_COLOR = "#002F5F";

/** Maps a periodicity cadence to the translation key of its time unit. */
const PERIODICITY_UNIT_KEY: Record<string, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  yearly: "year",
};

/**
 * Render a task's recurrence as "<n> <unit>" (e.g. "1 week", "2 years") using
 * the backend's `periodicity_multiple`, instead of the raw cadence label. Falls
 * back to the translated cadence word for unknown periodicities.
 */
export const formatPeriodicity = (
  t: TFunction,
  periodicity: string | null | undefined,
  multiple: number | null | undefined
): string => {
  if (!periodicity) return "-";
  const unitKey = PERIODICITY_UNIT_KEY[periodicity.toLowerCase()];
  if (!unitKey) {
    return t(`taskHub.modal.${periodicity.toLowerCase()}`) || periodicity;
  }
  const count = multiple && multiple > 0 ? multiple : 1;
  return `${count} ${t(`taskHub.modal.${unitKey}`)}`;
};

/** The user's theme color from localStorage, with a sensible default. */
export const getThemeColor = (): string => {
  try {
    return JSON.parse(
      localStorage.getItem("color-theme") || `"${DEFAULT_THEME_COLOR}"`
    );
  } catch {
    return DEFAULT_THEME_COLOR;
  }
};

/** The user's timezone from localStorage, defaulting to UTC. */
export const getTimezone = (): string =>
  localStorage.getItem("timezone") || "UTC";

/**
 * Map an array of relation objects to their ids. Handles both the
 * `{ <key>: { id } }` shape (expanded relations) and a plain `{ id }` shape.
 */
export const extractIds = (
  arr: Array<Record<string, any>> | undefined,
  key: string
): number[] => arr?.map((item) => item[key]?.id ?? item.id) || [];

export interface BuildTaskPayloadOptions {
  showDescription?: boolean;
  /** When provided, sets `parent_section` on the payload (create/move case). */
  parentSectionId?: number | null;
}

/**
 * Build the request body shared by the create and edit task flows. The two
 * modals previously duplicated this object verbatim.
 */
export const buildTaskPayload = (
  values: Record<string, any>,
  opts: BuildTaskPayloadOptions = {}
): Record<string, unknown> => {
  const isPeriodic = values.trigger === TASK_TRIGGER_PERIODICALLY;

  const payload: Record<string, unknown> = {
    name: values.name,
    trigger: values.trigger ?? null,
    periodicity: values.periodicity || null,
    periodicity_multiple: values.periodicity_multiple ?? null,
    is_recurrent: values.is_recurrent || false,
    start_periodicity: values.start_periodicity
      ? dayjs(values.start_periodicity).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
      : null,
    machines: isPeriodic ? [] : values.machines || [],
    product_cards: isPeriodic ? [] : values.product_cards || [],
    enabled: values.enabled,
    priority: values.priority,
    users: values.users || [],
    description: opts.showDescription ? values.description : null,
    companies: isPeriodic ? [] : values.companies ?? [],
  };

  if ("parentSectionId" in opts) {
    payload.parent_section = opts.parentSectionId ?? null;
  }

  return payload;
};
